import { gettext } from "i18n";
import { formatValueForTable } from "utils/formatting";

/**
 * Data processing for the "Manual/lab data (table)" dashboard widget.
 *
 * Kept free of React so that the grouping rules can be unit tested. The
 * param-based layouts group sources by *parameter* (not by source UUID), so
 * that e.g. "Al, aluminium [µg/L]" measured at several stations ends up in one
 * table with one column/row per station.
 */

export type ManualLabLayoutOption =
  | "station_params_rows"
  | "station_dates_rows"
  | "param_dates_rows"
  | "param_stations_rows";

export interface ManualLabSource {
  source_uuid: any | null;
  name: string | null;
  system_name?: string | null;
  unit: { symbol: string } | null;
  station: { id: number; name: string; identifier: string } | null;
}

export interface ManualLabDatapoint {
  timestamp: any | null;
  value: any | null;
  invalid: boolean | null;
}

export interface ManualLabSourceData {
  source: ManualLabSource;
  data: ManualLabDatapoint[];
}

export interface DailyValues {
  dailyAverages: { [dateKey: string]: number };
  dailyCounts: { [dateKey: string]: number };
}

export interface ManualLabSourceRow extends DailyValues {
  sourceName: string | null;
  stationName: string;
  stationIdentifier: string;
  stationKey: string;
  unit: string;
  sourceUUID: string;
  paramKey: string;
}

export interface ManualLabParam {
  key: string;
  name: string | null;
  unit: string;
}

export interface ManualLabStation {
  key: string;
  name: string;
  identifier: string;
}

export interface ProcessedManualLabData {
  /** Dates with data in any source. */
  dateColumns: string[];
  /** Dates with data for one parameter, used by the param layouts. */
  getParamDateColumns: (paramKey: string) => string[];
  sourceRows: ManualLabSourceRow[];
  allParams: ManualLabParam[];
  allStations: ManualLabStation[];
  getCell: (paramKey: string, stationKey: string) => DailyValues | undefined;
}

/**
 * Normalizes a parameter name so that the variants produced by the different
 * system_name generators (Eurofins importer, manual creation, DB migration)
 * and minor display name differences compare equal: "Al, aluminium",
 * "al__aluminium" and "al,_aluminium" all become "al_aluminium".
 */
export const normalizeParameterName = (name: string): string =>
  name
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "_")
    .replace(/^_+|_+$/g, "");

/**
 * Units are compared case-sensitively (mS vs. MS), but NFKC folds lookalikes
 * such as the micro sign (U+00B5) and Greek mu (U+03BC).
 */
export const normalizeUnit = (unit: string | null | undefined): string =>
  (unit || "").normalize("NFKC").trim();

/**
 * Identity of a parameter across stations. Sources with the same key are
 * shown in the same table. The unit is part of the key so that values in
 * different units are never mixed in one table.
 */
export const getParameterKey = (source: ManualLabSource): string => {
  const name =
    normalizeParameterName(source.system_name || "") ||
    normalizeParameterName(source.name || "");
  if (!name) {
    // Nothing to group by; keep the source on its own.
    return `source:${source.source_uuid || ""}`;
  }
  return `${name}|${normalizeUnit(source.unit?.symbol)}`;
};

export const getStationKey = (source: ManualLabSource): string => {
  const station = source.station;
  if (station?.id !== undefined && station?.id !== null) {
    return `id:${station.id}`;
  }
  if (station?.identifier) {
    return `identifier:${station.identifier}`;
  }
  // Stations without id or identifier must not collapse into one column.
  return `source:${source.source_uuid || ""}`;
};

/**
 * Formats a date key (a UTC date, see toDateKey) like toLocaleDateString.
 * Formatted in UTC: in the browser's zone, west of UTC, the date would be
 * shown as the previous day.
 */
export const formatDateKey = (dateKey: string): string =>
  new Date(`${dateKey}T00:00:00Z`).toLocaleDateString("fi-FI", {
    timeZone: "UTC",
  });

/** Station column/row label; the identifier when the station has no name. */
export const getStationLabel = (station: ManualLabSource["station"]): string =>
  station?.name || station?.identifier || "";

/** UTC date of the timestamp, or null if it cannot be parsed. */
export const toDateKey = (timestamp: any): string | null => {
  if (timestamp === null || timestamp === undefined || timestamp === "") {
    return null;
  }
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString().split("T")[0];
};

/**
 * Numeric value of a datapoint, or null when it has none. Hasura sends
 * float8 NaN/Infinity as strings, and a null must not count as 0.
 */
export const toNumericValue = (value: any): number | null => {
  let numeric: number;
  if (typeof value === "number") {
    numeric = value;
  } else if (typeof value === "string" && value.trim() !== "") {
    numeric = Number(value);
  } else {
    return null;
  }
  return Number.isFinite(numeric) ? numeric : null;
};

export const processManualLabData = (
  sourcesData: ManualLabSourceData[],
): ProcessedManualLabData => {
  const datesWithData = new Set<string>();
  const sourceRows: ManualLabSourceRow[] = [];
  const params = new Map<string, ManualLabParam>();
  const paramDates = new Map<string, Set<string>>();
  const stations = new Map<string, ManualLabStation>();
  // Sums are kept so that several sources of the same parameter in the same
  // station are averaged over all of their values, like values within a day.
  const cellSums = new Map<
    string,
    {
      sums: { [dateKey: string]: number };
      counts: { [dateKey: string]: number };
    }
  >();

  sourcesData.forEach(({ source, data }) => {
    const sums: { [dateKey: string]: number } = {};
    const counts: { [dateKey: string]: number } = {};

    data.forEach((point) => {
      if (point.invalid) return;
      const value = toNumericValue(point.value);
      const dateKey = toDateKey(point.timestamp);
      if (value === null || dateKey === null) return;
      sums[dateKey] = (sums[dateKey] ?? 0) + value;
      counts[dateKey] = (counts[dateKey] ?? 0) + 1;
      datesWithData.add(dateKey);
    });

    const dailyAverages: { [dateKey: string]: number } = {};
    Object.keys(sums).forEach((dateKey) => {
      dailyAverages[dateKey] = sums[dateKey] / counts[dateKey];
    });

    const unit = source.unit?.symbol || "";
    const paramKey = getParameterKey(source);
    const stationKey = getStationKey(source);

    sourceRows.push({
      sourceName: source.name,
      stationName: getStationLabel(source.station),
      stationIdentifier: source.station?.identifier || "",
      stationKey,
      unit,
      sourceUUID: source.source_uuid || "",
      paramKey,
      dailyAverages,
      dailyCounts: { ...counts },
    });

    if (!params.has(paramKey)) {
      params.set(paramKey, { key: paramKey, name: source.name, unit });
      paramDates.set(paramKey, new Set());
    }
    const datesOfParam = paramDates.get(paramKey)!;
    Object.keys(sums).forEach((dateKey) => datesOfParam.add(dateKey));
    if (!stations.has(stationKey)) {
      stations.set(stationKey, {
        key: stationKey,
        name: getStationLabel(source.station),
        identifier: source.station?.identifier || "",
      });
    }

    const cellKey = `${paramKey}\u0000${stationKey}`;
    const cell = cellSums.get(cellKey) ?? { sums: {}, counts: {} };
    Object.keys(sums).forEach((dateKey) => {
      cell.sums[dateKey] = (cell.sums[dateKey] ?? 0) + sums[dateKey];
      cell.counts[dateKey] = (cell.counts[dateKey] ?? 0) + counts[dateKey];
    });
    cellSums.set(cellKey, cell);
  });

  const cells = new Map<string, DailyValues>();
  cellSums.forEach(({ sums, counts }, cellKey) => {
    const dailyAverages: { [dateKey: string]: number } = {};
    Object.keys(sums).forEach((dateKey) => {
      dailyAverages[dateKey] = sums[dateKey] / counts[dateKey];
    });
    cells.set(cellKey, { dailyAverages, dailyCounts: counts });
  });

  const paramDateColumns = new Map<string, string[]>();
  paramDates.forEach((dates, paramKey) => {
    paramDateColumns.set(paramKey, Array.from(dates).sort());
  });

  return {
    dateColumns: Array.from(datesWithData).sort(),
    getParamDateColumns: (paramKey) => paramDateColumns.get(paramKey) ?? [],
    sourceRows,
    allParams: Array.from(params.values()),
    allStations: Array.from(stations.values()),
    getCell: (paramKey, stationKey) =>
      cells.get(`${paramKey}\u0000${stationKey}`),
  };
};

export const formatParamTitle = (name: string | null, unit: string): string =>
  `${name ?? ""}${unit ? ` [${unit}]` : ""}`;

/** Tabs and line breaks inside a cell would break the pasted table. */
const toClipboardCell = (text: string): string =>
  text.replace(/[\t\r\n]+/g, " ").trim();

const sourceRowLabel = (row: ManualLabSourceRow, withStation: boolean) => {
  const title = formatParamTitle(row.sourceName, row.unit);
  return withStation && row.stationName
    ? `${row.stationName}: ${title}`
    : title;
};

/** Tab separated contents of the table, for pasting into a spreadsheet. */
export const buildManualLabClipboardText = (
  processedData: ProcessedManualLabData,
  layoutOption: string,
): string => {
  const separator = "\t";
  const lines: string[] = [];
  const pushRow = (cells: string[]) =>
    lines.push(cells.map(toClipboardCell).join(separator));
  const formatDate = formatDateKey;
  // Same rule as the rendered table: station names are shown only when the
  // widget has sources from more than one station.
  const withStation = processedData.allStations.length > 1;

  switch (layoutOption) {
    case "station_dates_rows": {
      pushRow([
        gettext("Date"),
        ...processedData.sourceRows.map((r) => sourceRowLabel(r, withStation)),
      ]);
      processedData.dateColumns.forEach((dateKey) => {
        pushRow([
          formatDate(dateKey),
          ...processedData.sourceRows.map((sourceRow) =>
            formatValueForTable(sourceRow.dailyAverages[dateKey]),
          ),
        ]);
      });
      break;
    }
    case "station_params_rows": {
      pushRow([
        gettext("Source"),
        ...processedData.dateColumns.map(formatDate),
      ]);
      processedData.sourceRows.forEach((sourceRow) => {
        pushRow([
          sourceRowLabel(sourceRow, withStation),
          ...processedData.dateColumns.map((dateKey) =>
            formatValueForTable(sourceRow.dailyAverages[dateKey]),
          ),
        ]);
      });
      break;
    }
    case "param_dates_rows": {
      processedData.allParams.forEach((param) => {
        pushRow([formatParamTitle(param.name, param.unit)]);
        pushRow([
          gettext("Date"),
          ...processedData.allStations.map((station) => station.name),
        ]);
        processedData.getParamDateColumns(param.key).forEach((dateKey) => {
          pushRow([
            formatDate(dateKey),
            ...processedData.allStations.map((station) =>
              formatValueForTable(
                processedData.getCell(param.key, station.key)?.dailyAverages[
                  dateKey
                ],
              ),
            ),
          ]);
        });
        lines.push("");
      });
      break;
    }
    case "param_stations_rows": {
      processedData.allParams.forEach((param) => {
        const dateColumns = processedData.getParamDateColumns(param.key);
        pushRow([formatParamTitle(param.name, param.unit)]);
        pushRow([gettext("Station"), ...dateColumns.map(formatDate)]);
        processedData.allStations.forEach((station) => {
          const cell = processedData.getCell(param.key, station.key);
          pushRow([
            station.name,
            ...dateColumns.map((dateKey) =>
              formatValueForTable(cell?.dailyAverages[dateKey]),
            ),
          ]);
        });
        lines.push("");
      });
      break;
    }
  }

  return lines.join("\n");
};
